import { dispatchEvent, getConfig, fnl, hasTouch } from "../utils";
import store from "../store";
import { initBuyBonus } from "../store/slices/buy-bonus";
import { initBetBoost } from "../store/slices/bet-boost";
import { setStake } from "../store/slices/selectors";
import {
  toggleButtonsAvailability,
  setButtonType,
} from "../store/slices/buttons";
import {
  openModal,
  delayedCloseModal,
  delayedOpenModal,
  closeModal,
  ModalType,
} from "../store/slices/modals";
import {
  showFreeRounds,
  activateFreeRounds,
  declineFreeRounds,
} from "../store/slices/free-rounds";
import { resetHistory, setHistoryLoading } from "../store/slices/history";
import { removeFromInfo, addToInfo } from "../store/slices/selectors";
import { setAutoplay } from "../store/slices/autoplay";
import {
  setGameName,
  setLobby,
  setBank,
  startTimer,
  setLoaded,
  toggleHistory,
} from "../store/slices/game-details";
import { AutoplayLimit, EVENTS } from "./Types";
import { FR_STORAGE_KEY } from "../components/modals/FreeRounds";

// methods available globally in the project but not exposed outside of it,
// i.e. not available through window.Elysium.UI on production
const PrivateAPI = {
  setGameName(name: string) {
    store.dispatch(setGameName(name));
  },

  setStake(value: number) {
    store.dispatch(setStake(value));
    dispatchEvent(EVENTS.STAKE_SELECTED, value);
  },

  setLobby(lobby: string | null) {
    store.dispatch(setLobby(lobby || ""));
  },

  setBank(bankUrl: string | null) {
    store.dispatch(setBank(bankUrl || ""));
  },

  openModal(modal: ModalType, delay = 0) {
    const subModals = ["help", "pays", "history", "buyBonus", "selectBuyBonus"];
    store.dispatch(closeModal(subModals.includes(modal)));

    if (subModals.includes(modal)) {
      store.dispatch(delayedOpenModal(modal, delay));
    }

    if (modal === "history") {
      store.dispatch(resetHistory());
      const state = store.getState();
      PrivateAPI.getHistoryRounds([state.history.page, state.history.page + 1]);
    }
  },

  closeModal(modal: ModalType, delay = 0) {
    store.dispatch(delayedCloseModal(modal, delay));
  },

  getHistoryRound(roundId: string) {
    dispatchEvent(EVENTS.GET_HISTORY_ROUND, roundId);
  },

  getHistoryRounds(page: number | number[] = 0) {
    if (page < 0) return;
    store.dispatch(setHistoryLoading());

    dispatchEvent(EVENTS.GET_HISTORY_ROUNDS, page);
  },

  startAutoplay(value: number, limit?: AutoplayLimit) {
    store.dispatch(setAutoplay({ value, limit }));
    dispatchEvent(EVENTS.AUTOPLAY_STARTED);
    store.dispatch(setButtonType({ button: "spin", type: "stop" }));
  },

  showFreeRounds(): boolean {
    const state = store.getState().freeRounds;
    // const wasDeclined =
    //   window.localStorage && !!window.localStorage.getItem(FR_STORAGE_KEY);
    // if (wasDeclined || !state.amount || state.active) return false;
    // NOTE: The wasDeclined only make sence when there is a decline button
    if (!state.amount || state.active) return false;

    store.dispatch(openModal("freeRounds"));
    store.dispatch(showFreeRounds());

    return true;
  },

  activateFreeRounds(amount: number, stake: number) {
    if (!amount || !stake) return;

    const { selectors, buttons } = store.getState();
    const cache = {
      autoplay: buttons.autoplay.available,
      stake: selectors.stake,
      buyBonus: buttons.buyBonus.available,
    };

    store.dispatch(activateFreeRounds({ amount, stake, cache }));
    PrivateAPI.setStake(stake);

    store.dispatch(setAutoplay({ value: 0 })); // Stopping the autoplay if active;
    store.dispatch(
      toggleButtonsAvailability({
        stake: false,
        autoplay: false,
        buyBonus: false,
      })
    );
    store.dispatch(closeModal());
    dispatchEvent(EVENTS.FREE_ROUNDS_ACCEPTED);
  },

  declineFreeRounds(declined: boolean) {
    if (declined) {
      dispatchEvent(EVENTS.FREE_ROUNDS_DECLINED);
      window.localStorage &&
        window.localStorage.setItem(FR_STORAGE_KEY, "true");
    } else {
      dispatchEvent(EVENTS.FREE_ROUNDS_POSTPONED);
    }

    store.dispatch(closeModal());
    store.dispatch(declineFreeRounds());
  },

  startTimer(time?: string | number) {
    if (!time) {
      store.dispatch(startTimer(Date.now()));
    } else {
      store.dispatch(
        startTimer(typeof time === "string" ? parseInt(time) : time)
      );
    }
  },

  toggleHistory(value: boolean) {
    if (store.getState().details.history === value) return;
    if (value) {
      store.dispatch(addToInfo({ value: "history", text: "history" }));
    } else {
      store.dispatch(removeFromInfo("history"));
    }
    store.dispatch(toggleHistory(value));
  },

  setAsLoaded() {
    if (store.getState().details.loaded) return;

    store.dispatch(setLoaded(true));
    dispatchEvent(EVENTS.UI_LOADED);
  },

  async initBetBoost(
    available: boolean,
    rtp: { value: number; level: number } | undefined
  ) {
    const config = await getConfig();
    if (!available || !rtp || !rtp.level || !config || !config.betBoost) {
      store.dispatch(toggleButtonsAvailability({ betBoost: false }));
      return;
    }
    const action = Object.keys(config.betBoost)[0];
    const cfg = config.betBoost[action];
    const value = cfg.multipliers[rtp.level] || 0;
    const rtpValue = cfg.rtps[rtp.level] || 0;

    store.dispatch(
      initBetBoost({ action, value, rtp: rtpValue, tooltip: hasTouch() })
    );
  },

  async initBuyBonus(
    available: boolean,
    rtp: { value: number; level: number } | undefined
  ) {
    if (!available || !rtp || !rtp.level) {
      store.dispatch(toggleButtonsAvailability({ buyBonus: false }));
      return;
    }

    const state = store.getState();
    const config = await getConfig();
    const buyBonus = config.buyBonus ?? {};

    const data = Object.keys(buyBonus).reduce((o, name) => {
      const multiplier = buyBonus[name].multipliers[rtp.level];
      const bonusRTP = buyBonus[name].rtps[rtp.level];
      if (!multiplier) return o;

      const stakes = state.selectors.stake.possibleValues;
      const wagers = stakes.map(
        (stake) => (stake * 100 * (multiplier * 100)) / 10000
      );
      const stakeMap = fnl.zipToObj(stakes, wagers);
      const id = name;

      o[name] = { id, multiplier, rtp: bonusRTP, stakeMap };

      return o;
    }, {} as any);

    store.dispatch(
      toggleButtonsAvailability({ buyBonus: Object.keys(data).length > 0 })
    );
    store.dispatch(initBuyBonus(data));
  },

  // disable or enable the sound of the start of the game
  soundChoice() {
    dispatchEvent(EVENTS.SOUND_CHOICE);
  },
};

export default PrivateAPI;
